import { getPlainTextFromRichText } from '../../utils'
import { ERROR_CODE_TO_TRANSLATION_KEY } from '../donation-form/donation-service'
import { PlexForGoodTranslations } from '../../translations'

// Updated SimulationResponseType to include all error scenarios
export type SimulationResponseType =
    | 'success'
    // Field errors
    | 'error-plex'
    | 'error-campaign'
    | 'error-character'
    | 'error-invalid-amount'
    // Global errors
    | 'error-server'
    | 'error-session-expired'
    | 'error-duplicate-donation'
    | 'error-system-unavailable'
    | 'error-unexpected'
    | null

// Map of simulation types to error codes
export const SIMULATION_TO_ERROR_CODE: Record<string, string> = {
    'error-plex': 'DonationInsufficientBalance',
    'error-campaign': 'DonationInvalidCampaign',
    'error-character': 'DonationInvalidCharacter',
    'error-invalid-amount': 'DonationInvalidAmount',
    'error-server': 'DonationProcessingError',
    'error-session-expired': 'Unauthorized',
    'error-duplicate-donation': 'DonationDuplicateAttempt',
    'error-system-unavailable': 'SystemServiceUnavailable',
    'error-unexpected': 'SystemUnexpectedError',
}

// User-friendly descriptions of simulation types
export const SIMULATION_DESCRIPTIONS: Record<string, string> = {
    success: 'Successful Donation',
    'error-plex': 'Insufficient PLEX Balance',
    'error-campaign': 'Invalid Campaign ID',
    'error-character': 'Invalid Character',
    'error-invalid-amount': 'Invalid Amount',
    'error-server': 'Server Error',
    'error-session-expired': 'Session Expired',
    'error-duplicate-donation': 'Duplicate Donation',
    'error-system-unavailable': 'Service Unavailable',
    'error-unexpected': 'Unexpected Error',
}

/**
 * Gets the error message from translations based on error code
 */
const getErrorMessage = (
    errorCode: string,
    translations?: PlexForGoodTranslations
): string => {
    // Default messages if translations are not available
    const defaultMessages: Record<string, string> = {
        DonationInsufficientBalance: 'You do not have enough PLEX',
        DonationInvalidCampaign: 'Invalid campaign',
        DonationInvalidCharacter: 'Invalid character',
        DonationInvalidAmount: 'Invalid donation amount',
        DonationProcessingError:
            'The server is experiencing issues. Please try again later.',
        Unauthorized: 'Your session has expired. Please log in again.',
        DonationDuplicateAttempt: 'An unexpected error occurred.',
        SystemServiceUnavailable:
            'The service is currently unavailable. Please try again later.',
        SystemUnexpectedError: 'An unexpected error occurred.',
    }

    // If translations are available, use them
    if (translations) {
        const translationKey =
            ERROR_CODE_TO_TRANSLATION_KEY[errorCode] ||
            ERROR_CODE_TO_TRANSLATION_KEY._default
        if (translationKey && translations[translationKey]) {
            return getPlainTextFromRichText(translations[translationKey])
        }
    }

    // Fallback to default messages
    return defaultMessages[errorCode] || defaultMessages.SystemUnexpectedError
}

/**
 * Simulates a donation response for testing and debugging
 */
export const simulateDonation = (
    type: Exclude<SimulationResponseType, null>,
    onStart: () => void,
    onComplete: (
        success: boolean,
        errors?: unknown,
        message?: string,
        errorCode?: string
    ) => void,
    translations?: PlexForGoodTranslations
): void => {
    // Reset states and show loading
    onStart()

    // Simulate API delay
    setTimeout(() => {
        if (type === 'success') {
            onComplete(true)
            return
        }

        const errorCode =
            SIMULATION_TO_ERROR_CODE[type] || 'SystemUnexpectedError'
        const errorMessage = getErrorMessage(errorCode, translations)

        // Check if this is a field-specific error
        const isFieldError =
            type.startsWith('error-') &&
            [
                'error-plex',
                'error-character',
                'error-invalid-amount',
            ].includes(type)

        if (isFieldError) {
            // Map to appropriate field
            const fieldMap: Record<string, string> = {
                'error-plex': 'plexAmount',
                'error-character': 'characterId',
                'error-invalid-amount': 'plexAmount',
            }

            const field = fieldMap[type]
            const fieldErrors = { [field]: errorMessage }

            // Field-specific error doesn't show global message
            onComplete(false, fieldErrors, '', null)
        } else {
            // Global error
            onComplete(false, {}, errorMessage, errorCode)
        }
    }, 1000)
}
